package dev.lingvo.shared.screenshots.color

import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt

/** Perceptual coordinates allow frame finishes and palette derivation to share one colour space. */
data class OkLab(val l: Double, val a: Double, val b: Double)

/** Cylindrical OKLab uses hue in degrees so templates can vary hue without changing lightness. */
data class OkLch(val l: Double, val c: Double, val h: Double)

/** Converts opaque sRGB with IEC 61966-2-1 gamma and the reference OKLab matrices. */
fun srgbHexToOkLab(hex: String): OkLab {
    require(Regex("^#[0-9a-fA-F]{6}$").matches(hex)) { "Expected #RRGGBB" }
    fun channel(start: Int): Double {
        val value = hex.substring(start, start + 2).toInt(16) / 255.0
        return if (value <= 0.04045) value / 12.92 else ((value + 0.055) / 1.055).pow(2.4)
    }
    val r = channel(1)
    val g = channel(3)
    val b = channel(5)
    val ll = (0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b).pow(1.0 / 3)
    val mm = (0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b).pow(1.0 / 3)
    val ss = (0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b).pow(1.0 / 3)
    return OkLab(
        0.2104542553 * ll + 0.7936177850 * mm - 0.0040720468 * ss,
        1.9779984951 * ll - 2.4285922050 * mm + 0.4505937099 * ss,
        0.0259040371 * ll + 0.7827717662 * mm - 0.8086757660 * ss,
    )
}

/** Returns null for colours outside sRGB rather than silently clipping a derived palette. */
fun OkLab.toSrgbHex(): String? {
    if (!l.isFinite() || !a.isFinite() || !b.isFinite() || l !in 0.0..1.0) return null
    val ll = (l + 0.3963377774 * a + 0.2158037573 * b).pow(3)
    val mm = (l - 0.1055613458 * a - 0.0638541728 * b).pow(3)
    val ss = (l - 0.0894841775 * a - 1.2914855480 * b).pow(3)
    val channels = listOf(
        4.0767416621 * ll - 3.3077115913 * mm + 0.2309699292 * ss,
        -1.2684380046 * ll + 2.6097574011 * mm - 0.3413193965 * ss,
        -0.0041960863 * ll - 0.7034186147 * mm + 1.7076147010 * ss,
    )
    // The published inverse matrices introduce up to 1e-7 linear noise at exact gamut boundaries.
    if (channels.any { !it.isFinite() || it < -1e-6 || it > 1 + 1e-6 }) return null
    return "#" + channels.joinToString("") { raw ->
        val value = raw.coerceIn(0.0, 1.0)
        val encoded = if (value <= 0.0031308) value * 12.92 else 1.055 * value.pow(1 / 2.4) - 0.055
        (encoded * 255).roundToInt().coerceIn(0, 255).toString(16).uppercase().padStart(2, '0')
    }
}

/** Keeps hue canonical in [0, 360) for repeatable palette calculations. */
fun OkLab.toLch(): OkLch = OkLch(l, sqrt(a * a + b * b), (atan2(b, a) * 180 / PI + 360) % 360)

/** Converts a template's hue and chroma back to coordinates used by the gamut check. */
fun OkLch.toLab(): OkLab = OkLab(l, c * cos(h * PI / 180), c * sin(h * PI / 180))
